/**
 * Configuration Management Page (Admin Only)
 * 
 * Manages system configuration:
 * - Event types management
 * - Locations management
 * - Groups management
 * - Admin-only access restriction
 * 
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6
 */

import { useCallback, useEffect, useState } from 'react';
import { restClient, EventType, Location, Group, Rider } from '../../api/rest-client';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Modal } from '../../components/common/Modal';
import { useGroupLeaderStore } from '../../store/groupLeaderStore';
import SeasonManagement from './SeasonManagement';
import { TrailConfigurationManagement } from '../../components/features/configuration/TrailConfigurationManagement';
import { BulkEvents } from '../../components/features/bulkEvents/BulkEvents';
import { SkillsConfiguration } from '../../components/features/skills/SkillsConfiguration';
import { TrainingSeasonSettings } from '../../components/features/training-plans/TrainingSeasonSettings';

/**
 * Configuration Section Type
 */
type ConfigSection = 'eventTypes' | 'locations' | 'groups' | 'seasons' | 'timeTrials' | 'bulkEvents' | 'skills' | 'trainingPlans';

/**
 * Event type colors for calendar (matching Calendar page)
 */
const EVENT_TYPE_COLORS: Record<string, string> = {
  'Practice': '#5090f7',      // Blue
  'Race': '#ef4444',          // Red
  'Time Trial': '#f59e0b',    // Orange
  'Meeting': '#8b5cf6',       // Purple
  'Social': '#22c55e',        // Green
  'Other': '#435d7d',         // Dark blue
};

/**
 * Protected event types that cannot be deleted or renamed.
 * Other fields (description, color) can still be updated.
 */
const PROTECTED_EVENT_TYPES = ['Race', 'Practice'];

/**
 * Event Type Form Component
 */
interface EventTypeFormProps {
  eventType?: EventType;
  onSubmit: (data: Omit<EventType, 'type_id'>) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function EventTypeForm({ eventType, onSubmit, onCancel, isLoading }: EventTypeFormProps) {
  const isProtected = eventType ? PROTECTED_EVENT_TYPES.includes(eventType.name) : false;
  const [formData, setFormData] = useState({
    name: eventType?.name || '',
    description: eventType?.description || '',
    color: eventType?.color || '#435d7d',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Auto-set color when name matches a predefined event type
      if (name === 'name' && EVENT_TYPE_COLORS[value] && !eventType) {
        newData.color = EVENT_TYPE_COLORS[value];
      }
      
      return newData;
    });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          disabled={isProtected}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          } ${isProtected ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
          placeholder="Practice"
        />
        {isProtected && (
          <p className="mt-1 text-sm text-gray-500">This is a system event type and cannot be renamed.</p>
        )}
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Regular practice session"
        />
      </div>

      <div>
        <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">
          Color
        </label>
        
        {/* Predefined color swatches */}
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-2">Quick select (Calendar colors):</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(EVENT_TYPE_COLORS).map(([typeName, color]) => (
              <button
                key={typeName}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, color }))}
                className={`flex items-center gap-2 px-3 py-1.5 rounded border-2 transition-all ${
                  formData.color === color
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                title={`${typeName} - ${color}`}
              >
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: color }}
                />
                <span className="text-xs font-medium text-gray-700">{typeName}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Custom color picker */}
        <div className="flex items-center gap-3">
          <input
            type="color"
            id="color"
            name="color"
            value={formData.color}
            onChange={handleChange}
            className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
          />
          <span className="text-sm text-gray-600">{formData.color}</span>
          <span className="text-xs text-gray-500">or choose custom color</span>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="submit" variant="primary" disabled={isLoading} className="flex-1">
          {isLoading ? 'Saving...' : eventType ? 'Update' : 'Create'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * Location Form Component
 */
interface LocationFormProps {
  location?: Location;
  onSubmit: (data: Omit<Location, 'location_id'>) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function LocationForm({ location, onSubmit, onCancel, isLoading }: LocationFormProps) {
  const [formData, setFormData] = useState({
    name: location?.name || '',
    address: location?.address || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="USC Track"
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
          Address
        </label>
        <textarea
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="123 Main St, Los Angeles, CA 90001"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="submit" variant="primary" disabled={isLoading} className="flex-1">
          {isLoading ? 'Saving...' : location ? 'Update' : 'Create'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * Group Form Component
 */
interface GroupFormProps {
  group?: Group;
  coaches?: Rider[];
  onSubmit: (data: Omit<Group, 'group_id'>) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

export function GroupForm({ group, coaches = [], onSubmit, onCancel, isLoading }: GroupFormProps) {
  const [formData, setFormData] = useState({
    name: group?.name || '',
    order: group?.order || 0,
    group_leader: group?.group_leader || '',
    group_skill_level: group?.group_skill_level || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'order' ? parseInt(value) || 0 : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Varsity"
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="order" className="block text-sm font-medium text-gray-700 mb-1">
          Order *
        </label>
        <input
          type="number"
          id="order"
          name="order"
          value={formData.order}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="0"
          min="0"
        />
      </div>

      <div>
        <label htmlFor="group_leader" className="block text-sm font-medium text-gray-700 mb-1">
          Group Leader
        </label>
        <select
          id="group_leader"
          name="group_leader"
          value={formData.group_leader}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Select Group Leader</option>
          {coaches.map(coach => (
            <option key={coach.rider_id} value={coach.rider_id}>
              {coach.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="group_skill_level" className="block text-sm font-medium text-gray-700 mb-1">
          Skill Level
        </label>
        <select
          id="group_skill_level"
          name="group_skill_level"
          value={formData.group_skill_level}
          onChange={handleChange}
          data-testid="group-form-skill-level-select"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">None</option>
          <option value="Beginner">Beginner</option>
          <option value="Intermediate">Intermediate</option>
          <option value="Advanced">Advanced</option>
        </select>
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="submit" variant="primary" disabled={isLoading} className="flex-1">
          {isLoading ? 'Saving...' : group ? 'Update' : 'Create'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * Delete Confirmation Modal
 */
interface DeleteConfirmationProps {
  item: { name: string } | null;
  itemType: string;
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function DeleteConfirmation({ item, itemType, isOpen, onConfirm, onCancel, isLoading }: DeleteConfirmationProps) {
  if (!item) return null;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={`Delete ${itemType}`}>
      <div className="space-y-4">
        <p className="text-gray-700">
          Are you sure you want to delete <strong>{item.name}</strong>?
        </p>
        <p className="text-sm text-red-600">
          This action cannot be undone.
        </p>
        <div className="flex gap-3 pt-4">
          <Button variant="danger" onClick={onConfirm} disabled={isLoading} className="flex-1">
            {isLoading ? 'Deleting...' : `Delete ${itemType}`}
          </Button>
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Configuration Management Page Component
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6
 */
export function Configuration() {
  // State
  const [activeSection, setActiveSection] = useState<ConfigSection>('eventTypes');
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [coaches, setCoaches] = useState<Rider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fetchGroupLeaders = useGroupLeaderStore(s => s.fetchGroupLeaders);

  /**
   * Fetch all configuration data
   * Requirements: 8.1, 8.2, 8.3
   */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [eventTypesData, locationsData, groupsData] = await Promise.all([
        restClient.getEventTypes(),
        restClient.getLocations(),
        restClient.getGroups(),
      ]);

      // Load active coaches for group leader dropdown
      const [seasonConfig, allRiders] = await Promise.all([
        restClient.getSeasonConfig(),
        restClient.getRiders(),
      ]);
      const currentSeason = seasonConfig.currentSeason;
      const activeCoaches = allRiders
        .filter((r: Rider) => {
          const isCoach = r.type === 'Coach';
          const isNotDisabled = r.disabled !== '1' && r.disabled !== 1;
          const isCurrentSeason = r.season === currentSeason ||
            (r.seasons_active && r.seasons_active.includes(currentSeason));
          return isCoach && isNotDisabled && isCurrentSeason;
        })
        .sort((a: Rider, b: Rider) => a.name.localeCompare(b.name));

      setEventTypes(eventTypesData);
      setLocations(locationsData);
      setGroups(groupsData);
      setCoaches(activeCoaches);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Handle create item
   * Requirement 8.5
   */
  const handleCreate = useCallback(() => {
    setSelectedItem(null);
    setIsFormModalOpen(true);
  }, []);

  /**
   * Handle edit item
   * Requirement 8.5
   */
  const handleEdit = useCallback((item: any) => {
    setSelectedItem(item);
    setIsFormModalOpen(true);
  }, []);

  /**
   * Handle delete item
   * Requirement 8.5
   */
  const handleDelete = useCallback((item: any) => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  }, []);

  /**
   * Submit form (create or update)
   * Requirement 8.5
   */
  const handleSubmit = useCallback(async (data: any) => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (activeSection === 'eventTypes') {
        if (selectedItem) {
          await restClient.updateEventType(selectedItem.type_id, data);
        } else {
          await restClient.createEventType(data);
        }
      } else if (activeSection === 'locations') {
        if (selectedItem) {
          await restClient.updateLocation(selectedItem.location_id, data);
        } else {
          await restClient.createLocation(data);
        }
      } else if (activeSection === 'groups') {
        if (selectedItem) {
          await restClient.updateGroup(selectedItem.group_id, data);
        } else {
          await restClient.createGroup(data);
        }
      }

      await fetchData();
      setIsFormModalOpen(false);
      setSelectedItem(null);
      // Refresh group leader store if groups were modified
      if (activeSection === 'groups') {
        fetchGroupLeaders();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSubmitting(false);
    }
  }, [activeSection, selectedItem, fetchData]);

  /**
   * Confirm delete
   * Requirement 8.5
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!selectedItem) return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (activeSection === 'eventTypes') {
        await restClient.deleteEventType(selectedItem.type_id);
      } else if (activeSection === 'locations') {
        await restClient.deleteLocation(selectedItem.location_id);
      } else if (activeSection === 'groups') {
        await restClient.deleteGroup(selectedItem.group_id);
      }

      await fetchData();
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
      // Refresh group leader store if a group was deleted
      if (activeSection === 'groups') {
        fetchGroupLeaders();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setIsSubmitting(false);
    }
  }, [activeSection, selectedItem, fetchData]);

  /**
   * Get current items based on active section
   */
  const getCurrentItems = () => {
    if (activeSection === 'eventTypes') {
      // Sort event types alphabetically by name
      return [...eventTypes].sort((a, b) => a.name.localeCompare(b.name));
    }
    if (activeSection === 'locations') {
      // Sort locations alphabetically by name
      return [...locations].sort((a, b) => a.name.localeCompare(b.name));
    }
    // Sort groups by order
    return [...groups].sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  /**
   * Get section title
   */
  const getSectionTitle = () => {
    if (activeSection === 'eventTypes') return 'Event Types';
    if (activeSection === 'locations') return 'Locations';
    return 'Groups';
  };

  /**
   * Get item type name
   */
  const getItemTypeName = () => {
    if (activeSection === 'eventTypes') return 'Event Type';
    if (activeSection === 'locations') return 'Location';
    return 'Group';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const currentItems = getCurrentItems();

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-3xl font-bold text-primary-900">Configuration</h1>
          <p className="text-primary-600 mt-1">Manage system configuration settings</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pt-3 pb-8">
        {/* Section Tabs */}
        <div className="mb-6 border-b border-gray-200 -mx-4 px-4 overflow-x-auto">
        <nav className="flex space-x-6 min-w-max">
          <button
            onClick={() => setActiveSection('eventTypes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeSection === 'eventTypes'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Event Types
          </button>
          <button
            onClick={() => setActiveSection('locations')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeSection === 'locations'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Locations
          </button>
          <button
            onClick={() => setActiveSection('groups')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeSection === 'groups'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Groups
          </button>
          <button
            onClick={() => setActiveSection('timeTrials')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeSection === 'timeTrials'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Time Trials
          </button>
          <button
            onClick={() => setActiveSection('bulkEvents')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeSection === 'bulkEvents'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Bulk Events
          </button>
          <button
            onClick={() => setActiveSection('seasons')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeSection === 'seasons'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Season Management
          </button>
          <button
            onClick={() => setActiveSection('skills')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeSection === 'skills'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Skills
          </button>
          <button
            onClick={() => setActiveSection('trainingPlans')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeSection === 'trainingPlans'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Training Season
          </button>
        </nav>
      </div>

      {/* Season Management Section */}
      {activeSection === 'seasons' ? (
        <SeasonManagement />
      ) : activeSection === 'timeTrials' ? (
        <TrailConfigurationManagement />
      ) : activeSection === 'bulkEvents' ? (
        <BulkEvents />
      ) : activeSection === 'skills' ? (
        <SkillsConfiguration />
      ) : activeSection === 'trainingPlans' ? (
        <TrainingSeasonSettings />
      ) : (
        <>
          {/* Section Header */}
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">{getSectionTitle()}</h2>
            <Button variant="primary" onClick={handleCreate}>
              Create {getItemTypeName()}
            </Button>
          </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Items List */}
      {currentItems.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-gray-900">No {getSectionTitle().toLowerCase()} found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new {getItemTypeName().toLowerCase()}
            </p>
            <div className="mt-6">
              <Button variant="primary" onClick={handleCreate}>
                Create {getItemTypeName()}
              </Button>
            </div>
          </div>
        </Card>
      ) : activeSection === 'groups' ? (
        // Table view for groups (matching legacy app)
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-primary-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Order
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Group Leader
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Skill Level
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-primary-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentItems.map((item: any) => (
                  <tr key={item.group_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{item.order}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">
                        {item.group_leader
                          ? (() => {
                              const coach = coaches.find((c: Rider) => c.rider_id === item.group_leader);
                              return coach ? <>{coach.name} ⭐</> : <span className="italic text-gray-400">{item.group_leader} (not found)</span>;
                            })()
                          : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{item.group_skill_level || '—'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-primary-600 hover:text-primary-900 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        // Card view for event types and locations
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentItems.map((item: any) => (
            <Card key={item.type_id || item.location_id || item.group_id} className="hover:shadow-lg transition-shadow">
              <div className="flex flex-col h-full">
                {/* Item Info */}
                <div className="flex-grow mb-4">
                  {activeSection === 'eventTypes' && item.color && (
                    <div className="mb-3">
                      <div
                        className="w-full h-2 rounded"
                        style={{ backgroundColor: item.color }}
                      />
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.name}</h3>
                  {item.description && (
                    <p className="text-sm text-gray-600">{item.description}</p>
                  )}
                  {item.address && (
                    <p className="text-sm text-gray-600 mt-2">{item.address}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-auto">
                  <Button variant="secondary" size="small" onClick={() => handleEdit(item)} className="flex-1">
                    Edit
                  </Button>
                  {!(activeSection === 'eventTypes' && PROTECTED_EVENT_TYPES.includes(item.name)) && (
                    <Button variant="danger" size="small" onClick={() => handleDelete(item)}>
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      </>
      )}

      {/* Form Modal */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setSelectedItem(null);
        }}
        title={selectedItem ? `Edit ${getItemTypeName()}` : `Create ${getItemTypeName()}`}
      >
        {activeSection === 'eventTypes' && (
          <EventTypeForm
            eventType={selectedItem}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsFormModalOpen(false);
              setSelectedItem(null);
            }}
            isLoading={isSubmitting}
          />
        )}
        {activeSection === 'locations' && (
          <LocationForm
            location={selectedItem}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsFormModalOpen(false);
              setSelectedItem(null);
            }}
            isLoading={isSubmitting}
          />
        )}
        {activeSection === 'groups' && (
          <GroupForm
            group={selectedItem}
            coaches={coaches}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsFormModalOpen(false);
              setSelectedItem(null);
            }}
            isLoading={isSubmitting}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        item={selectedItem}
        itemType={getItemTypeName()}
        isOpen={isDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setSelectedItem(null);
        }}
        isLoading={isSubmitting}
      />
      </div>
    </div>
  );
}

export default Configuration;
